import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { CanFrame } from '../../lib/canlog/types';
import type { EventMarker } from '../../lib/decode/eventMarkers';
import type { SignalHint } from '../../lib/decode/signalHints';
import {
  buildGraphSeries,
  getCanIdOptions
} from '../../lib/decode/graphSeries';
import { BYTE_COLORS, ByteSeriesChart, type ChartOverlaySeries } from './ByteSeriesChart';
import { SignalHintCard } from './SignalHintCard';

type SignalGraphPanelProps = {
  frames: CanFrame[];
  selectedCanId: number | null;
  onSelectCanId: (canId: number | null) => void;
  signalHintsByCanId: Record<number, SignalHint[]>;
  compact?: boolean;
  eventMarkers?: EventMarker[];
  focusedByteIndex?: number | null;
  focusedTimestampMs?: number | null;
  focusedByteLength?: number | null;
  overlays?: ChartOverlaySeries[];
  onZoomUsed?: () => void;
};

function formatMs(value: number | null) {
  if (value === null) return 'n/a';
  return `${value.toFixed(3)} ms`;
}

function formatHz(value: number | null) {
  return value === null ? 'n/a' : `${value.toFixed(2)} Hz`;
}

function isFrequencyReliable(series: ReturnType<typeof buildGraphSeries>) {
  if (!series || series.stats.approxHz === null || series.stats.firstTimestampMs === null || series.stats.lastTimestampMs === null) return true;
  const durationMs = series.stats.lastTimestampMs - series.stats.firstTimestampMs;
  return durationMs >= 1000 && series.stats.approxHz <= 500;
}

function formatByteRange(min: number | null, max: number | null) {
  if (min === null || max === null) return '--';
  return min === max ? String(min) : `${min}-${max}`;
}

export function SignalGraphPanel({
  frames,
  selectedCanId,
  onSelectCanId,
  signalHintsByCanId,
  compact = false,
  eventMarkers = [],
  focusedByteIndex = null,
  focusedTimestampMs = null,
  focusedByteLength = null,
  overlays = [],
  onZoomUsed
}: SignalGraphPanelProps) {
  const [enabledBytes, setEnabledBytes] = useState(() => Array.from({ length: 8 }, () => true));
  const lastZoomTrackRef = useRef(0);
  const canIdOptions = useMemo(() => getCanIdOptions(frames), [frames]);
  const series = useMemo(() => buildGraphSeries(frames, selectedCanId), [frames, selectedCanId]);
  const selectedHints = selectedCanId === null ? [] : signalHintsByCanId[selectedCanId] ?? [];
  const selectedOption = canIdOptions.find((option) => option.canId === selectedCanId);
  const frequencyReliable = isFrequencyReliable(series);

  function toggleByte(byteIndex: number) {
    setEnabledBytes((current) => current.map((enabled, index) => index === byteIndex ? !enabled : enabled));
  }

  function soloByte(byteIndex: number) {
    setEnabledBytes((current) => {
      const isSolo = current[byteIndex] && current.filter(Boolean).length === 1;
      return current.map((_, index) => isSolo || index === byteIndex);
    });
  }

  function handleZoomUsed() {
    const now = Date.now();
    if (now - lastZoomTrackRef.current < 1500) return;
    lastZoomTrackRef.current = now;
    onZoomUsed?.();
  }

  useEffect(() => {
    if (focusedByteIndex === null) return;
    const byteLength = Math.max(1, focusedByteLength ?? 1);
    setEnabledBytes(Array.from({ length: 8 }, (_, index) => index >= focusedByteIndex && index < focusedByteIndex + byteLength));
  }, [focusedByteIndex, focusedByteLength]);

  return (
    <section className={`section signal-graph-section ${compact ? 'compact' : ''}`}>
      <article className="data-panel wide signal-graph-panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Signal identification</p>
            <h2>Signal Graph</h2>
            <p className="panel-note">Select a CAN ID to inspect byte activity over time. Seeded matches are suggestions, not verified decodes.</p>
          </div>
          <span className="table-count">{canIdOptions.length.toLocaleString()} CAN IDs</span>
        </div>

        <div className="signal-graph-controls">
          <label>
            CAN ID
            <select
              value={selectedCanId ?? ''}
              onChange={(event) => onSelectCanId(event.target.value ? Number(event.target.value) : null)}
              disabled={canIdOptions.length === 0}
            >
              <option value="">Select a CAN ID</option>
              {canIdOptions.map((option) => (
                <option key={option.canId} value={option.canId}>
                  {option.canIdHex} - {option.frameCount.toLocaleString()} frames
                </option>
              ))}
            </select>
          </label>
          <div className="byte-toggle-grid" aria-label="Byte visibility toggles">
            {enabledBytes.map((enabled, byteIndex) => (
              <span className="byte-toggle-item" key={byteIndex}>
                <button
                  className={enabled ? 'active' : ''}
                  type="button"
                  onClick={() => toggleByte(byteIndex)}
                  style={{ '--byte-color': BYTE_COLORS[byteIndex] } as CSSProperties}
                  aria-pressed={enabled}
                >
                  B{byteIndex}
                </button>
                <button
                  className="byte-solo-button"
                  type="button"
                  onClick={() => soloByte(byteIndex)}
                  style={{ '--byte-color': BYTE_COLORS[byteIndex] } as CSSProperties}
                  aria-label={`Solo byte ${byteIndex}`}
                >
                  solo
                </button>
              </span>
            ))}
          </div>
        </div>

        <div className="graph-debug-row">
          <span>Selected CAN ID: <strong>{selectedOption?.canIdHex ?? series?.canId ?? 'none'}</strong></span>
          <span>Selected frame count: <strong>{selectedOption?.frameCount.toLocaleString() ?? series?.stats.frameCount.toLocaleString() ?? '0'}</strong></span>
          <span>Plotted points: <strong>{series?.points.length.toLocaleString() ?? '0'}</strong></span>
        </div>

        <div className="signal-graph-layout">
          <div className="signal-graph-main">
            <ByteSeriesChart
              series={series}
              enabledBytes={enabledBytes}
              eventMarkers={eventMarkers}
              focusedByteIndex={focusedByteIndex}
              focusedTimestampMs={focusedTimestampMs}
              overlays={overlays}
              onZoomUsed={handleZoomUsed}
            />
            {series && series.totalPoints > series.points.length && (
              <p className="panel-note">Showing {series.points.length.toLocaleString()} downsampled points from {series.totalPoints.toLocaleString()} frames.</p>
            )}
          </div>
          <aside className="signal-graph-side">
            <div className="status-grid signal-graph-stats">
              <div className="status-card">
                <small>Frame count</small>
                <strong title={series?.stats.frameCount.toLocaleString() ?? '0'}>
                  {series?.stats.frameCount.toLocaleString() ?? '0'}
                </strong>
              </div>
              <div className={frequencyReliable ? 'status-card' : 'status-card fps-warning-card'}>
                <small>Approx frequency</small>
                <strong title={frequencyReliable ? formatHz(series?.stats.approxHz ?? null) : 'Unreliable'}>
                  {frequencyReliable ? formatHz(series?.stats.approxHz ?? null) : 'Unreliable'}
                </strong>
                {!frequencyReliable && <span className="fps-warning-badge">Short timestamp span</span>}
              </div>
              <div className="status-card">
                <small>First timestamp</small>
                <strong title={formatMs(series?.stats.firstTimestampMs ?? null)}>
                  {formatMs(series?.stats.firstTimestampMs ?? null)}
                </strong>
              </div>
              <div className="status-card">
                <small>Last timestamp</small>
                <strong title={formatMs(series?.stats.lastTimestampMs ?? null)}>
                  {formatMs(series?.stats.lastTimestampMs ?? null)}
                </strong>
              </div>
            </div>
            <div className="byte-range-grid">
              {Array.from({ length: 8 }, (_, byteIndex) => (
                <div key={byteIndex}>
                  <small>B{byteIndex}</small>
                  <strong>{formatByteRange(series?.stats.byteMin[byteIndex] ?? null, series?.stats.byteMax[byteIndex] ?? null)}</strong>
                </div>
              ))}
            </div>
            <SignalHintCard hints={selectedHints} />
          </aside>
        </div>
      </article>
    </section>
  );
}
